import { debounce } from 'lodash'
import moment from 'moment'
import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { connect } from 'react-redux'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { bindActionCreators } from 'redux'

import { getCurrentSpaceId } from '@/components/App/selectors'
import ConnectorLogView from '@/components/ConnectorLog'
import ErrorConfirmation from '@/components/ErrorConfirmation'
import Page from '@/components/Page'
import { resetRequest } from '@/store/actions'
import { isRequestError, isRequestPending } from '@/store/selectors'

import { fetchConnectorInstance, fetchConnectorLogs, fetchCustomConnectorInstance } from './actions'
import { FETCH_CONNECTOR_LOGS, FETCH_CUSTOM_CONNECTOR_INSTANCE } from './actionTypes'
import messages from './messages'
import { getConnectorLogs, getCustomConnector, getOffset, getTotalCount } from './selectors'

function SpaceConnectorLogsPage(props: any) {
  const intl = useIntl()

  const history = useHistory()
  const location = useLocation()
  const params = useParams<{ appId?: string; connectorId?: string }>()

  const [state, _setState] = useState({
    searchParams: {
      spaceId: props.currentSpaceId,
      connectorId: params.connectorId,
      offset: 0,
      // Default start date to todays date -14 days (retention period)
      start: moment(new Date()).add(-14, 'days').startOf('day').toISOString(),
      end: moment(new Date()).toISOString()
    },
    logs: []
  })
  const setState = (update: Partial<Parameters<typeof _setState>[0]>) => {
    return _setState(state => ({ ...state, ...(typeof update === 'function' ? update(state) : update) }))
  }

  // componentDidMount
  useEffect(() => {
    props.actions.fetchConnectorLogs(state.searchParams)
    const itemId = params.appId || params.connectorId
    loadCurrentConnector(itemId, props.currentSpaceId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // componentWillUnmount
  useEffect(() => {
    return () => {
      props.actions.resetRequest([FETCH_CONNECTOR_LOGS, FETCH_CUSTOM_CONNECTOR_INSTANCE])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const searchLogs = debounce(params => {
    const searchParams = { ...state.searchParams, ...params }
    setState({ searchParams })
    props.actions.fetchConnectorLogs(state.searchParams)
  }, 300)

  function loadCurrentConnector(itemId: any, spaceId: any) {
    props.actions.fetchConnectorInstance(itemId, spaceId)
  }

  const goBackToApps = () => {
    const stripEditPath = stripPath(location.pathname, 4)
    history.push(stripEditPath + '/apps/installed')
  }

  function stripPath(path: any, count: any) {
    for (let i = 0; i < count; i++) {
      path = path.slice(0, Math.max(0, path.lastIndexOf('/')))
    }

    return path
  }

  const { logs, totalResults, offset, isLoading, currentConnector } = props
  return (
    <Page isLoading={!props.logs && props.isLoading} loadingMessage={<FormattedMessage {...messages.loading} />}>
      <>
        <Page.Header
          title={<FormattedMessage {...messages.title} />}
          goBack={goBackToApps}
          backNavTitle={<FormattedMessage {...messages.back} />}
        />
        {props.hasError ? (
          <ErrorConfirmation
            error={intl.formatMessage(messages.errorTitle)}
            subMessage={intl.formatMessage(messages.errorMessage)}
          />
        ) : (
          props.logs && (
            <div>
              <ConnectorLogView
                offset={offset}
                totalResults={totalResults}
                logs={logs}
                isActionInProgress={isLoading}
                perPage={20}
                searchLogs={searchLogs}
                searchParams={state.searchParams}
                currentConnector={currentConnector}
              />
            </div>
          )
        )}
      </>
    </Page>
  )
}

SpaceConnectorLogsPage.displayName = 'SpaceConnectorLogsPage'

SpaceConnectorLogsPage.propTypes = {
  actions: PropTypes.shape({
    fetchConnectorLogs: PropTypes.func.isRequired,
    fetchConnectorInstance: PropTypes.func.isRequired
  }).isRequired,
  logs: PropTypes.arrayOf(PropTypes.object).isRequired,
  currentSpaceId: PropTypes.string.isRequired,
  isLoading: PropTypes.bool
}

SpaceConnectorLogsPage.defaultProps = {
  logs: [],
  currentConnector: {},
  isLoading: false,
  offset: 0,
  totalResults: 0,
  perPage: 20,
  isSearching: false,
  isActionInProgress: false
}

const DisconnectedSpaceConnectorLogsPage = SpaceConnectorLogsPage
export { /** @deprecated */ DisconnectedSpaceConnectorLogsPage as SpaceConnectorLogsPage }

function mapStateToProps(state: any) {
  return {
    currentConnector: getCustomConnector(state),
    currentSpaceId: getCurrentSpaceId(state),
    logs: getConnectorLogs(state),
    totalResults: getTotalCount(state),
    isLoading: isRequestPending(state, FETCH_CONNECTOR_LOGS),
    offset: getOffset(state),
    hasError: isRequestError(state, [FETCH_CONNECTOR_LOGS])
  }
}

function mapDispatchToProps(dispatch: any) {
  return {
    actions: bindActionCreators(
      { fetchConnectorLogs, resetRequest, fetchConnectorInstance, fetchCustomConnectorInstance },
      dispatch
    )
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(DisconnectedSpaceConnectorLogsPage)
